import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Styled replacement for the browser `title` bubble. Mounted once (AppShell);
 * listens on `document`, so any element gets a tooltip by carrying `data-tip`.
 *
 * Plain `title` attributes still work: on first hover the text is moved into
 * `data-tip` and `title` is set to "" (never removed). That keeps React's own
 * attribute diff intact — when the `title` prop later becomes `undefined`,
 * React removes the attribute and we drop the stale tip; when it changes, we
 * re-adopt the new text. Prefer `data-tip` for text that changes often.
 *
 * Skipped: `title` on <svg>/<img>/<iframe> (native semantics) and anything
 * inside `.leaflet-container` (Leaflet owns its controls' titles).
 *
 * Disabled buttons emit no mouse events; wrap them in
 * `<span className="tip-wrap" data-tip="…">` (see styles.css).
 */
export const TOOLTIP_DELAY_MS = 350;
const OFFSET = 8;
const MARGIN = 8;
const ADOPTED = "tipFromTitle";
const NO_ADOPT = new Set(["SVG", "IMG", "IFRAME"]);

type Tip = { target: HTMLElement; text: string };
type Placement = { left: number; top: number; place: "top" | "bottom"; arrowX: number };

function canAdopt(el: HTMLElement): boolean {
  return !NO_ADOPT.has(el.tagName.toUpperCase()) && !el.closest(".leaflet-container");
}

/** Sync `data-tip` with a `title` set (or later cleared) by React. */
function syncTitle(el: HTMLElement): void {
  const title = el.getAttribute("title");
  if (title === null) {
    if (el.dataset[ADOPTED] !== undefined) {
      delete el.dataset[ADOPTED];
      el.removeAttribute("data-tip");
    }
    return;
  }
  if (title === "" || !canAdopt(el)) return;
  el.setAttribute("data-tip", title);
  el.setAttribute("title", "");
  el.dataset[ADOPTED] = "1";
  // Icon-only controls named by `title` alone would lose their accessible name.
  if (!el.hasAttribute("aria-label") && !el.hasAttribute("aria-labelledby") && !el.textContent?.trim()) {
    el.setAttribute("aria-label", title);
  }
}

/** Nearest ancestor (self included) that carries a tooltip text, or null. */
export function findTipTarget(node: EventTarget | null): HTMLElement | null {
  let el: Element | null = node instanceof Element ? node : null;
  if (el?.closest(".leaflet-container")) return null;
  while (el && el !== document.body) {
    if (el instanceof HTMLElement) {
      syncTitle(el);
      const text = el.getAttribute("data-tip");
      if (text !== null) return text.trim() ? el : null;
    }
    el = el.parentElement;
  }
  return null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let current: HTMLElement | null = null;
    let timer: number | null = null;

    const clearTimer = () => {
      if (timer !== null) { window.clearTimeout(timer); timer = null; }
    };
    const hide = () => {
      clearTimer();
      current = null;
      setTip(null);
    };
    const schedule = (el: HTMLElement) => {
      if (el === current) return;
      clearTimer();
      current = el;
      setTip(null);
      timer = window.setTimeout(() => {
        timer = null;
        const text = el.getAttribute("data-tip");
        if (el.isConnected && text && text.trim()) setTip({ target: el, text: text.trim() });
      }, TOOLTIP_DELAY_MS);
    };

    const onOver = (e: MouseEvent) => {
      const el = findTipTarget(e.target);
      if (el) schedule(el);
      else if (current) hide();
    };
    const onOut = (e: MouseEvent) => {
      if (!current) return;
      const to = e.relatedTarget;
      if (!(to instanceof Node) || !current.contains(to)) hide();
    };
    const onFocusIn = (e: FocusEvent) => {
      const el = findTipTarget(e.target);
      if (el) schedule(el);
    };
    const onFocusOut = () => { if (current) hide(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };
    const onDismiss = () => { if (current) hide(); };

    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("mousedown", onDismiss, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    return () => {
      clearTimer();
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("mousedown", onDismiss, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, []);

  // Measure after the box exists (hidden), then place it below the target, centred;
  // flip above when there is no room below; keep it inside the viewport.
  useLayoutEffect(() => {
    if (!tip) { setPlacement(null); return; }
    const box = boxRef.current;
    if (!box) return;
    const r = tip.target.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let place: Placement["place"] = "bottom";
    let top = r.bottom + OFFSET;
    if (top + b.height > vh - MARGIN && r.top - OFFSET - b.height >= MARGIN) {
      place = "top";
      top = r.top - OFFSET - b.height;
    }
    const left = clamp(r.left + r.width / 2 - b.width / 2, MARGIN, Math.max(MARGIN, vw - MARGIN - b.width));
    const arrowX = clamp(r.left + r.width / 2 - left, 10, Math.max(10, b.width - 10));
    setPlacement({ left, top, place, arrowX });
  }, [tip]);

  if (!tip) return null;
  const place = placement?.place ?? "bottom";
  return createPortal(
    <div
      ref={boxRef}
      role="tooltip"
      className={`tooltip is-${place}${placement ? " is-placed" : ""}`}
      style={{
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        ["--tip-arrow-x" as string]: placement ? `${placement.arrowX}px` : undefined,
      } as React.CSSProperties}
    >
      {tip.text}
    </div>,
    document.body,
  );
}
